import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, FileText } from "lucide-react";
import { Reveal } from "@/components/Reveal";

/**
 * Shared layout for placeholder legal pages (Privacy, Cookies, Terms).
 * These render a clean institutional placeholder until full legal copy
 * is approved and published.
 */
const LegalPage = ({ kicker, title, summary, sections = [], lastUpdated, testid }) => (
  <main className="bg-white" data-testid={testid}>
    <section className="pt-32 md:pt-40 pb-16 bg-radial-soft relative overflow-hidden">
      <div className="absolute inset-0 bg-grid opacity-50 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 md:px-8 relative">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#06102A]/65">
            {kicker}
          </p>
        </Reveal>
        <Reveal delay={80}>
          <h1 className="mt-6 font-display text-5xl md:text-6xl lg:text-7xl font-medium leading-[1.02] tracking-tight text-[#06102A] max-w-4xl">
            {title}
          </h1>
        </Reveal>
        <Reveal delay={140}>
          <p className="mt-7 max-w-2xl text-lg text-[#06102A]/70">{summary}</p>
        </Reveal>
        {lastUpdated && (
          <Reveal delay={200}>
            <p className="mt-8 font-mono-vt text-xs text-[#06102A]/55">
              Last updated · {lastUpdated}
            </p>
          </Reveal>
        )}
      </div>
    </section>

    <section className="py-20 md:py-24">
      <div className="max-w-3xl mx-auto px-6 md:px-8">
        {sections.map((s, i) => (
          <Reveal key={s.h} delay={(i % 4) * 60}>
            <article className="py-7 border-b border-[#06102A]/10 last:border-0">
              <h2 className="font-display text-2xl md:text-[26px] font-medium text-[#06102A] leading-tight">
                {s.h}
              </h2>
              <p className="mt-4 text-[15px] text-[#06102A]/75 leading-relaxed whitespace-pre-line">
                {s.b}
              </p>
            </article>
          </Reveal>
        ))}

        <Reveal>
          <div className="mt-12 rounded-2xl border border-[#06102A]/10 bg-[#FAFBFC] p-7 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-xl bg-[#06102A]/[0.04] grid place-items-center flex-shrink-0">
                <FileText className="h-4 w-4 text-[#06102A]" strokeWidth={1.7} />
              </div>
              <div>
                <p className="font-display text-lg font-medium text-[#06102A]">
                  Need a complete or signed version of this document?
                </p>
                <p className="mt-1 text-[14px] text-[#06102A]/65">
                  Request the full text or a counter-signed copy via the Trust Centre.
                </p>
              </div>
            </div>
            <Link
              to="/trust-centre#documentation-requests"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#06102A] text-white text-sm font-medium px-6 py-3 hover:-translate-y-0.5 transition-all whitespace-nowrap"
            >
              Open Request Form
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </Reveal>

        <Reveal delay={80}>
          <div className="mt-10">
            <Link
              to="/trust-centre"
              className="inline-flex items-center gap-2 text-sm font-medium text-[#06102A] hover:text-[#3B82F6] transition-colors group"
            >
              <ArrowRight className="h-4 w-4 -rotate-180 transition-transform group-hover:-translate-x-1" />
              Back to Trust Centre
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  </main>
);

export default LegalPage;
